import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Calculator, Upload, Sparkles, Loader2, Home, FileImage, Store, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/lib/store";
import { inr } from "@/components/badges";
import { ToolResultList } from "@/components/ToolResultList";

const TABS = [
  { id: "calculator", label: "Material Calculator", icon: Calculator, blurb: "Room size → exact quantities" },
  { id: "floorplan", label: "Floor Plan → BOQ", icon: Home, blurb: "Upload plan → whole-house order" },
  { id: "bom", label: "Bulk Order Builder", icon: Upload, blurb: "Paste or upload BOM → cart" },
  { id: "ai", label: "AI Recommender", icon: Sparkles, blurb: "Describe project → materials list" },
];

function CalculatorTool() {
  const [f, setF] = useState({ length_ft: 12, width_ft: 10, height_ft: 10, wardrobes: 1, kitchen_ft: 0, include_flooring: false, include_ceiling: false });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const num = (k) => (e) => setF({ ...f, [k]: Number(e.target.value) });
  const run = async (e) => {
    e.preventDefault(); setBusy(true);
    try { setRes((await api.post("/tools/calculator", f)).data); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-8">
      <form onSubmit={run} className="card-surface p-6 space-y-4 h-fit" data-testid="calc-form">
        {[["length_ft", "Room length (ft)"], ["width_ft", "Room width (ft)"], ["height_ft", "Wall height (ft)"], ["wardrobes", "No. of wardrobes (7ft)"], ["kitchen_ft", "Kitchen running ft"]].map(([k, l]) => (
          <label key={k} className="block text-xs font-semibold text-slate-600">{l}
            <input type="number" min="0" step="0.5" value={f[k]} onChange={num(k)} data-testid={`calc-${k}`} className="input-b2b mt-1" />
          </label>
        ))}
        {[["include_flooring", "Include floor tiles"], ["include_ceiling", "Include false ceiling + ceiling paint"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`calc-${k}`} className="h-4 w-4 accent-amber-500" /> {l}
          </label>
        ))}
        <button type="submit" disabled={busy} data-testid="calc-submit" className="btn-brand w-full !py-3">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Calculate quantities"}</button>
      </form>
      <div>
        {res ? (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="card-surface p-4"><p className="overline-label">Wall area</p><p className="font-display text-2xl font-bold mt-1" data-testid="calc-wall-area">{res.wall_area_sqft} sqft</p></div>
              <div className="card-surface p-4"><p className="overline-label">Floor area</p><p className="font-display text-2xl font-bold mt-1">{res.floor_area_sqft} sqft</p></div>
            </div>
            <ToolResultList items={res.lines} total={res.estimated_total} testid="calc-results" source="calculator" />
          </>
        ) : <Empty text="Enter room dimensions to get a quantity-wise material list with live catalog prices." />}
      </div>
    </div>
  );
}

function BomTool() {
  const [text, setText] = useState("sku,qty\nPLY-18MR-CEN,4\nLAM-1MM-MER,5\nHettich hinges,12\nFevicol Marine,1");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const onFile = (e) => { const file = e.target.files?.[0]; if (!file) return; const r = new FileReader(); r.onload = () => setText(String(r.result)); r.readAsText(file); };
  const run = async () => {
    setBusy(true);
    try { setRes((await api.post("/tools/bom", { text })).data); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-8">
      <div className="card-surface p-6 space-y-4 h-fit">
        <p className="text-sm text-slate-600">One line per item: <span className="font-mono2 text-xs bg-slate-100 px-1.5 py-0.5 rounded">SKU or product name, quantity</span></p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} data-testid="bom-textarea" className="input-b2b font-mono2 !text-xs" />
        <label className="btn-ghost-b2b w-full cursor-pointer"><Upload className="h-4 w-4" /> Upload CSV<input type="file" accept=".csv,.txt" onChange={onFile} className="hidden" data-testid="bom-file-input" /></label>
        <button onClick={run} disabled={busy} data-testid="bom-submit" className="btn-brand w-full !py-3">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Match against catalog"}</button>
      </div>
      <div>
        {res ? (
          <>
            {res.unmatched.length > 0 && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="bom-unmatched">
                Not found: {res.unmatched.map((u) => u.input).join(", ")} — request these via the onboarding form.
              </div>
            )}
            <ToolResultList items={res.matched} total={res.matched.reduce((s, m) => s + m.product.price * m.qty, 0)} testid="bom-results" source="bom" />
          </>
        ) : <Empty text="Paste your BOM or upload a CSV; we match SKUs and names to in-stock catalog items." />}
      </div>
    </div>
  );
}

function AiTool() {
  const [desc, setDesc] = useState("");
  const [budget, setBudget] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault(); setBusy(true); setRes(null);
    try { setRes((await api.post("/tools/recommend", { description: desc, budget })).data); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-8">
      <form onSubmit={run} className="card-surface p-6 space-y-4 h-fit">
        <textarea required value={desc} onChange={(e) => setDesc(e.target.value)} rows={7} data-testid="ai-description"
          placeholder="e.g. 3BHK in Whitefield. L-shaped modular kitchen 11 rft with tall unit, 2 sliding wardrobes 7ft, TV unit with fluted panels, matte walnut + white theme."
          className="input-b2b" />
        <input value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="Budget (optional), e.g. ₹3 lakh" data-testid="ai-budget" className="input-b2b" />
        <button type="submit" disabled={busy} data-testid="ai-submit" className="btn-brand w-full !py-3">{busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Building your list…</> : <><Sparkles className="h-4 w-4" /> Recommend materials</>}</button>
        <p className="text-[11px] text-slate-500">Powered by GPT-5.4 · picks only from live Bengaluru vendor catalog.</p>
      </form>
      <div>
        {busy && <Empty text="Our estimator is reading your brief and sizing quantities…" />}
        {res && (
          <>
            <div className="card-surface p-4 mb-4 border-l-4 !border-l-amber-500" data-testid="ai-summary"><p className="text-sm text-slate-700 leading-relaxed">{res.summary}</p></div>
            <ToolResultList items={res.items} total={res.estimated_total} testid="ai-results" source="ai" />
          </>
        )}
        {!busy && !res && <Empty text="Describe the project in plain words — rooms, finishes, units — and get a shoppable materials list." />}
      </div>
    </div>
  );
}

const Empty = ({ text }) => <div className="card-surface border-dashed p-12 text-center text-sm text-slate-500" data-testid="tool-empty">{text}</div>;

function FloorPlanTool() {
  const { user } = useStore();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({ zones: [] });
  const [confirm, setConfirm] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [form, setForm] = useState({ zone_id: "", address: "", phone: "", payment_method: "cod" });

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (user) setForm((o) => ({ ...o, address: o.address || user.address || "", phone: o.phone || user.phone || "", zone_id: o.zone_id || user.zone_id || "" }));
  }, [user]);

  const onFile = (e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setRes(null); } };

  const analyse = async () => {
    if (!file) { toast.error("Choose a floor plan image or PDF first"); return; }
    if (!user) { toast.info("Please sign in to generate a floor-plan BOQ."); navigate("/auth"); return; }
    setBusy(true); setRes(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tools/floorplan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes(data);
      if (!data.vendor_groups?.length) toast.error("No catalog materials could be matched from this plan. Try a clearer floor plan.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const itemCount = res ? res.vendor_groups.reduce((s, g) => s + g.items.length, 0) : 0;

  const openConfirm = () => {
    if (!user) { toast.info("Please sign in as a buyer to place the order."); navigate("/auth"); return; }
    if (user.role !== "buyer") { toast.error("Only buyer accounts can place orders."); return; }
    setConfirm(true);
  };

  const placeOrders = async () => {
    if (!form.address.trim()) { toast.error("Enter a delivery address"); return; }
    if (!form.zone_id) { toast.error("Select a delivery zone"); return; }
    setPlacing(true);
    try {
      const groups = res.vendor_groups.map((g) => ({ vendor_id: g.vendor_id, items: g.items.map((i) => ({ product_id: i.product.id, qty: i.qty })) }));
      const { data } = await api.post("/orders/boq", { groups, zone_id: form.zone_id, address: form.address, phone: form.phone, payment_method: form.payment_method });
      toast.success(`${data.orders.length} order(s) placed from your floor plan`);
      if (data.errors?.length) toast.info(`${data.errors.length} vendor group(s) skipped (out of stock).`);
      setConfirm(false);
      navigate("/orders");
    } catch (err) { toast.error(formatApiError(err)); } finally { setPlacing(false); }
  };

  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-8">
      <div className="card-surface p-6 space-y-4 h-fit" data-testid="floorplan-form">
        <p className="text-sm text-slate-600">Upload a floor plan (image or PDF). Our AI reads each room, estimates areas, and builds a whole-house material list ready to order.</p>
        <label className="btn-ghost-b2b w-full cursor-pointer" data-testid="floorplan-upload-label">
          <FileImage className="h-4 w-4" /> {file ? "Change file" : "Choose floor plan"}
          <input type="file" accept="image/*,.pdf,application/pdf" onChange={onFile} className="hidden" data-testid="floorplan-file-input" />
        </label>
        {file && <p className="text-xs text-slate-500 truncate" data-testid="floorplan-filename">Selected: {file.name}</p>}
        <button onClick={analyse} disabled={busy || !file} data-testid="floorplan-analyse-btn" className="btn-brand w-full !py-3">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Reading plan…</> : <><Home className="h-4 w-4" /> Generate BOQ</>}
        </button>
        <p className="text-[11px] text-slate-500">Powered by Gemini vision · matched to live Bengaluru vendor catalog. Quantities are estimates — review before ordering.</p>
      </div>

      <div>
        {busy && <Empty text="Analysing your floor plan — detecting rooms and sizing materials…" />}
        {res && (
          <div className="space-y-4" data-testid="floorplan-results">
            {res.summary && <div className="card-surface p-4 border-l-4 !border-l-amber-500"><p className="text-sm text-slate-700 leading-relaxed" data-testid="floorplan-summary">{res.summary}</p></div>}
            {res.rooms?.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="floorplan-rooms">
                {res.rooms.map((r, i) => (
                  <span key={i} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{r.name} · {Math.round(r.area_sqft)} sqft</span>
                ))}
              </div>
            )}
            {res.vendor_groups.map((g) => (
              <div key={g.vendor_id} className="card-surface overflow-hidden" data-testid={`floorplan-group-${g.vendor_id}`}>
                <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="flex items-center gap-2 text-sm font-bold text-slate-900"><Store className="h-4 w-4 text-orange-700" /> {g.vendor_name}</p>
                  <p className="text-sm font-semibold text-slate-700">{g.items.length} items · {inr(g.subtotal)}</p>
                </div>
                <div className="divide-y divide-slate-200">
                  {g.items.map((it, idx) => (
                    <div key={idx} className="p-3 flex items-center gap-3" data-testid={`floorplan-item-${g.vendor_id}-${idx}`}>
                      <img src={it.product.image} alt="" className="h-12 w-12 rounded-lg object-cover bg-slate-100 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">{it.product.name}</p>
                        <p className="text-xs text-slate-500 truncate">{it.label || it.product.category}{it.room ? ` · ${it.room}` : ""}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-slate-900">{it.qty} × {inr(it.product.price)}</p>
                        <p className="text-[11px] text-slate-500">/{it.product.unit}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {res.vendor_groups.length > 0 && (
              <div className="card-surface p-5 flex flex-wrap items-center justify-between gap-4 sticky bottom-4" data-testid="floorplan-order-bar">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Estimated materials total (excl. GST & delivery)</p>
                  <p className="font-display text-2xl font-bold text-slate-900" data-testid="floorplan-total">{inr(res.estimated_total)}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{itemCount} items across {res.vendor_groups.length} vendor(s) · one order per vendor</p>
                </div>
                <button onClick={openConfirm} data-testid="floorplan-order-btn" className="btn-brand !py-3">
                  Place {res.vendor_groups.length} order{res.vendor_groups.length > 1 ? "s" : ""}
                </button>
              </div>
            )}
          </div>
        )}
        {!busy && !res && <Empty text="Upload your floor plan to get a full-house material list (BOQ) you can order in one go." />}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="floorplan-confirm-modal">
          <div className="w-full max-w-lg card-surface p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-xl font-bold text-slate-900">Confirm your order</h3>
                <p className="text-sm text-slate-500 mt-1">{res.vendor_groups.length} separate order(s) — one per vendor — totalling about {inr(res.estimated_total)} before GST & delivery.</p>
              </div>
              <button onClick={() => setConfirm(false)} data-testid="floorplan-confirm-close" className="p-1 text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Delivery zone</label>
                <select data-testid="floorplan-zone-select" value={form.zone_id} onChange={(e) => setForm({ ...form, zone_id: e.target.value })} className="input-b2b mt-2">
                  <option value="">Select zone</option>
                  {meta.zones.map((z) => <option key={z.id} value={z.id}>{z.name}{z.express_supported ? "" : " (scheduled only)"}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Delivery address</label>
                <textarea data-testid="floorplan-address-input" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Site address with landmark" className="input-b2b mt-2" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">WhatsApp number for order alerts</label>
                <input data-testid="floorplan-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98xxxxxxx" className="input-b2b mt-2" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Payment</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {[["cod", "Cash on delivery"], ["credit", "Credit terms"]].map(([v, l]) => (
                    <button key={v} data-testid={`floorplan-payment-${v}`} onClick={() => setForm({ ...form, payment_method: v })}
                      className={`rounded-lg border px-3 py-2.5 text-xs font-semibold ${form.payment_method === v ? "border-orange-700 bg-orange-50 text-orange-800" : "border-slate-300 text-slate-600"}`}>{l}</button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-slate-500">Online payment can be made per order from the Orders page after placing.</p>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setConfirm(false)} className="btn-ghost-b2b flex-1" data-testid="floorplan-confirm-cancel">Cancel</button>
              <button onClick={placeOrders} disabled={placing} data-testid="floorplan-confirm-place-btn" className="btn-brand flex-1 !py-3">
                {placing ? "Placing…" : `Confirm & place ${res.vendor_groups.length} order${res.vendor_groups.length > 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ToolsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "calculator";
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tools-page">
      <p className="overline-label">Procurement tools</p>
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">Plan the order before you place it</h1>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setParams({ tab: t.id })} data-testid={`tools-tab-${t.id}`}
            className={`text-left rounded-xl border p-4 transition-colors ${tab === t.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}>
            <t.icon className={`h-5 w-5 ${tab === t.id ? "text-amber-400" : "text-orange-700"}`} />
            <p className="font-display font-semibold mt-2 text-sm">{t.label}</p>
            <p className={`text-xs mt-0.5 ${tab === t.id ? "text-slate-300" : "text-slate-500"}`}>{t.blurb}</p>
          </button>
        ))}
      </div>
      <div className="mt-8">
        {tab === "calculator" && <CalculatorTool />}
        {tab === "floorplan" && <FloorPlanTool />}
        {tab === "bom" && <BomTool />}
        {tab === "ai" && <AiTool />}
      </div>
    </div>
  );
}
